import { Card, Statistic, Row, Col, Tag } from "antd";
import {
  TrophyOutlined,
  ShopOutlined,
  DollarOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { HospitalityAnalytics } from "../interfaces/hospitalityAnalytics.interface";

interface HospitalitySegmentStatsProps {
  segments: HospitalityAnalytics["segments"];
  isMobile: boolean;
}

export default function HospitalitySegmentStats({
  segments,
  isMobile,
}: HospitalitySegmentStatsProps) {
  const formatCurrency = (amount: number) => {
    if (typeof amount !== "number" || isNaN(amount)) {
      return "€0.00";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number, decimals: number = 1) => {
    if (typeof num !== "number" || isNaN(num)) {
      return "0";
    }
    return num.toFixed(decimals);
  };

  return (
    <Row gutter={[16, 16]}>
      {/* VIP Segment */}
      <Col xs={24} sm={12} md={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #fa8c16",
            background: "linear-gradient(135deg, #fff5e6 0%, #ffffff 100%)",
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Tag color="orange" icon={<TrophyOutlined />} style={{ fontSize: "14px", padding: "4px 12px" }}>
              VIP
            </Tag>
          </div>
          <Statistic
            title="Customers"
            value={segments.vip.count}
            valueStyle={{ color: "#fa8c16", fontSize: isMobile ? "20px" : "28px" }}
          />
          <Statistic
            title="Revenue"
            value={formatCurrency(segments.vip.revenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#3f8600", marginTop: 8 }}
          />
          <Statistic
            title="Avg Visit Frequency"
            value={formatNumber(segments.vip.averageVisitFrequency)}
            suffix="/month"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: "#13c2c2", marginTop: 8, fontSize: "14px" }}
          />
          <Statistic
            title="Avg Lifetime Value"
            value={formatCurrency(segments.vip.averageLifetimeValue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#722ed1", marginTop: 8, fontSize: "14px" }}
          />
        </Card>
      </Col>

      {/* Regular Segment */}
      <Col xs={24} sm={12} md={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #1890ff",
            background: "linear-gradient(135deg, #e6f7ff 0%, #ffffff 100%)",
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Tag color="blue" icon={<ShopOutlined />} style={{ fontSize: "14px", padding: "4px 12px" }}>
              Regular
            </Tag>
          </div>
          <Statistic
            title="Customers"
            value={segments.regular.count}
            valueStyle={{ color: "#1890ff", fontSize: isMobile ? "20px" : "28px" }}
          />
          <Statistic
            title="Revenue"
            value={formatCurrency(segments.regular.revenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#3f8600", marginTop: 8 }}
          />
          <Statistic
            title="Avg Visit Frequency"
            value={formatNumber(segments.regular.averageVisitFrequency)}
            suffix="/month"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: "#13c2c2", marginTop: 8, fontSize: "14px" }}
          />
          <Statistic
            title="Avg Lifetime Value"
            value={formatCurrency(segments.regular.averageLifetimeValue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#722ed1", marginTop: 8, fontSize: "14px" }}
          />
        </Card>
      </Col>

      {/* Occasional Segment */}
      <Col xs={24} sm={12} md={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #722ed1",
            background: "linear-gradient(135deg, #f9f0ff 0%, #ffffff 100%)",
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Tag color="purple" icon={<ShopOutlined />} style={{ fontSize: "14px", padding: "4px 12px" }}>
              Occasional
            </Tag>
          </div>
          <Statistic
            title="Customers"
            value={segments.occasional.count}
            valueStyle={{ color: "#722ed1", fontSize: isMobile ? "20px" : "28px" }}
          />
          <Statistic
            title="Revenue"
            value={formatCurrency(segments.occasional.revenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#3f8600", marginTop: 8 }}
          />
          <Statistic
            title="Avg Visit Frequency"
            value={formatNumber(segments.occasional.averageVisitFrequency)}
            suffix="/month"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: "#13c2c2", marginTop: 8, fontSize: "14px" }}
          />
          <Statistic
            title="Avg Lifetime Value"
            value={formatCurrency(segments.occasional.averageLifetimeValue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#722ed1", marginTop: 8, fontSize: "14px" }}
          />
        </Card>
      </Col>

      {/* One-Time Segment */}
      <Col xs={24} sm={12} md={12} lg={6}>
        <Card
          style={{
            borderLeft: "4px solid #faad14",
            background: "linear-gradient(135deg, #fffbe6 0%, #ffffff 100%)",
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Tag color="gold" icon={<ShopOutlined />} style={{ fontSize: "14px", padding: "4px 12px" }}>
              One-Time
            </Tag>
          </div>
          <Statistic
            title="Customers"
            value={segments.oneTime.count}
            valueStyle={{ color: "#faad14", fontSize: isMobile ? "20px" : "28px" }}
          />
          <Statistic
            title="Revenue"
            value={formatCurrency(segments.oneTime.revenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#3f8600", marginTop: 8 }}
          />
          <Statistic
            title="Avg Visit Frequency"
            value={formatNumber(segments.oneTime.averageVisitFrequency)}
            suffix="/month"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: "#13c2c2", marginTop: 8, fontSize: "14px" }}
          />
          <Statistic
            title="Avg Lifetime Value"
            value={formatCurrency(segments.oneTime.averageLifetimeValue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#722ed1", marginTop: 8, fontSize: "14px" }}
          />
        </Card>
      </Col>
    </Row>
  );
}

